import React, { useEffect, useState } from "react";
import { Label } from "../../../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../../components/ui/select";

const NO_ASSET = "__none__";

/**
 * AssetVersionSlotField — EMAILS-IMPL-011
 *
 * One governed Creative-composition slot: "choose an Asset, then choose
 * its EXACT immutable Version" -- never just an Asset ID. Reused for all
 * 9 Fingerprint slots (Subject/Preview/Hook/Email Body/Offer/CTA/P.S./
 * Landing Page/Image) rather than duplicating this two-Select interaction
 * 9 times; each caller supplies its own family's real list/version-history
 * API functions (SubjectsPanel.jsx etc. already established these same
 * functions for the Assets catalog itself -- reused here unchanged, no
 * second Asset data layer).
 *
 * ELIGIBILITY (checkpoint §12): the Asset dropdown is FILTERED to
 * backend-eligible statuses only (`eligibleStatuses`, passed by the
 * caller from compositionSlots.js's own table, mirroring
 * email_marketing_creative_authoring_service.py's own status gate
 * exactly) -- never a frontend-only Challenger/experiment exception, and
 * never an option the backend would reject on submit.
 *
 * VERSION DEFAULTING (checkpoint §10): selecting an Asset defaults
 * `version` to that Asset's OWN current_version (already present on the
 * list row -- no extra request needed for this) -- but the exact string
 * is always visibly shown in the Version Select, and the operator can
 * change it to any version in that Asset's real immutable history
 * (fetched lazily, only once an Asset is actually selected).
 */
export default function AssetVersionSlotField({
  label,
  required,
  value, // { assetId, version } | null
  onChange,
  listFn,
  versionsFn,
  idField,
  eligibleStatuses,
  testPrefix,
}) {
  const [assets, setAssets] = useState([]);
  const [assetsStatus, setAssetsStatus] = useState("loading"); // loading | ready | unavailable

  useEffect(() => {
    // EMAILS-IMPL-029: this component is reused across Canvas block
    // selections without remounting (no per-block key) -- `listFn`/
    // `eligibleStatuses` change identity whenever the operator selects a
    // block of a DIFFERENT family (compositionSlots.js's own stable,
    // per-family function/Set references). Both MUST be dependencies:
    // an empty array here previously froze `assets` at whichever family
    // was selected first, so a later selection of a different family
    // kept showing the first family's options -- e.g. a Hook title
    // rendered in the Email Body/Offer/CTA selector.
    let cancelled = false;
    setAssets([]);
    setAssetsStatus("loading");
    (async () => {
      try {
        const rows = await listFn();
        if (!cancelled) {
          setAssets((rows || []).filter((row) => eligibleStatuses.has(row.status)));
          setAssetsStatus("ready");
        }
      } catch {
        if (!cancelled) setAssetsStatus("unavailable");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [listFn, eligibleStatuses]);

  const [versions, setVersions] = useState([]);
  const [versionsStatus, setVersionsStatus] = useState("idle"); // idle | loading | ready | unavailable

  useEffect(() => {
    if (!value?.assetId) {
      setVersions([]);
      setVersionsStatus("idle");
      return;
    }
    let cancelled = false;
    setVersionsStatus("loading");
    (async () => {
      try {
        const history = await versionsFn(value.assetId);
        if (!cancelled) {
          setVersions(history || []);
          setVersionsStatus("ready");
        }
      } catch {
        if (!cancelled) setVersionsStatus("unavailable");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [value?.assetId, versionsFn]);

  const selectAsset = (assetId) => {
    if (assetId === NO_ASSET) {
      onChange(null);
      return;
    }
    const asset = assets.find((a) => a[idField] === assetId);
    onChange({ assetId, version: asset?.current_version || "" });
  };

  const selectVersion = (version) => {
    onChange({ ...value, version });
  };

  return (
    <div className="space-y-2 rounded-md border border-white/10 p-3">
      <Label htmlFor={`${testPrefix}-asset-select`}>
        {label} {required ? <span className="text-rose-400">*</span> : <span className="text-slate-500">(optional)</span>}
      </Label>
      {assetsStatus === "unavailable" && (
        <p className="text-xs text-amber-300" data-testid={`${testPrefix}-unavailable`}>
          Unable to load {label} catalog right now.
        </p>
      )}
      <Select value={value?.assetId || NO_ASSET} onValueChange={selectAsset} disabled={assetsStatus !== "ready"}>
        <SelectTrigger id={`${testPrefix}-asset-select`} data-testid={`${testPrefix}-asset-select`}>
          <SelectValue placeholder={assetsStatus === "loading" ? "Loading…" : `Select ${label}`} />
        </SelectTrigger>
        <SelectContent>
          {!required && <SelectItem value={NO_ASSET} data-testid={`${testPrefix}-asset-option-none`}>None</SelectItem>}
          {assets.map((a) => (
            <SelectItem key={a[idField]} value={a[idField]} data-testid={`${testPrefix}-asset-option-${a[idField]}`}>
              {a.name} ({a[idField]})
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {value?.assetId && (
        <div className="space-y-1.5">
          <Label htmlFor={`${testPrefix}-version-select`}>{label} Version</Label>
          <Select value={value.version || ""} onValueChange={selectVersion} disabled={versionsStatus === "loading"}>
            <SelectTrigger id={`${testPrefix}-version-select`} data-testid={`${testPrefix}-version-select`}>
              <SelectValue placeholder={versionsStatus === "loading" ? "Loading versions…" : "Select a Version"} />
            </SelectTrigger>
            <SelectContent>
              {/* The Asset's current_version is always offered even before
                  the full history has loaded, so the default selection is
                  never blank -- the operator sees and can keep it. */}
              {value.version && !versions.some((v) => v.version === value.version) && (
                <SelectItem value={value.version} data-testid={`${testPrefix}-version-option-${value.version}`}>
                  {value.version} (current)
                </SelectItem>
              )}
              {versions.map((v) => (
                <SelectItem key={v.version} value={v.version} data-testid={`${testPrefix}-version-option-${v.version}`}>
                  {v.version}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}
